import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../lib/api';

// Dummy doctor type (replace with real type from your API)
interface Doctor {
  id: string;
  user: { firstName: string; lastName: string };
}

interface TestReport {
  id: string;
  fileName: string;
  filePath: string;
  uploadedAt: string;
  description?: string;
  doctorAccess: { doctor: Doctor }[];
  comments: { comment: string; doctor: Doctor; createdAt: string }[];
}

const PatientTestReports: React.FC = () => {
  const [reports, setReports] = useState<TestReport[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
    fetchDoctors();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await api.get('/test-reports');
      setReports(res.data.data || []);
    } catch (e) {
      console.error('Failed to load reports:', e);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await api.get('/doctors');
      setDoctors(res.data.data.doctors || []);
    } catch (e) {
      console.error('Failed to load doctors:', e);
      toast.error('Failed to load doctors');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDoctorSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = Array.from(e.target.selectedOptions).map(opt => opt.value);
    setSelectedDoctors(options);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file');
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    selectedDoctors.forEach(id => formData.append('doctorIds', id));
    try {
      await api.post('/test-reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Report uploaded successfully');
      setFile(null);
      setDescription('');
      setSelectedDoctors([]);
      fetchReports();
    } catch (e) {
      console.error('Upload failed:', e);
      toast.error('Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-8">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3 mt-4">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-8">
      <h2 className="text-xl font-semibold mb-4">Test Reports</h2>
      <form onSubmit={handleUpload} className="mb-6 flex flex-col md:flex-row gap-4 items-end">
        <input 
          type="file" 
          onChange={handleFileChange} 
          className="border p-2 rounded" 
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        />
        <input 
          type="text" 
          value={description} 
          onChange={e => setDescription(e.target.value)} 
          placeholder="Description (optional)" 
          className="border p-2 rounded flex-1" 
        />
        <select 
          multiple 
          value={selectedDoctors} 
          onChange={handleDoctorSelect} 
          className="border p-2 rounded min-w-[200px]"
        >
          {doctors.map(doc => (
            <option key={doc.id} value={doc.id}>
              {doc.user.firstName} {doc.user.lastName}
            </option>
          ))}
        </select>
        <button 
          type="submit" 
          disabled={uploading} 
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-400"
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </form>
      <div>
        {reports.length === 0 ? (
          <div className="text-gray-500">No reports uploaded yet.</div>
        ) : (
          <div className="space-y-6">
            {reports.map(report => (
              <div key={report.id} className="border rounded p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <div className="font-medium">{report.fileName}</div>
                    <div className="text-xs text-gray-500">
                      Uploaded: {new Date(report.uploadedAt).toLocaleString()}
                    </div>
                    {report.description && (
                      <div className="text-sm text-gray-700 mt-1">{report.description}</div>
                    )}
                  </div>
                  <a 
                    href={`${api.defaults.baseURL}/files/${report.filePath}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Download
                  </a>
                </div>
                <div className="mt-2 text-sm text-gray-700">
                  <span className="font-semibold">Shared with doctors:</span>{' '}
                  {report.doctorAccess.map(da => 
                    `${da.doctor.user.firstName} ${da.doctor.user.lastName}`
                  ).join(', ') || 'None'}
                </div>
                <div className="mt-2">
                  <span className="font-semibold">Doctor Comments:</span>
                  {report.comments.length === 0 ? (
                    <span className="ml-2 text-gray-500">No comments yet.</span>
                  ) : (
                    <ul className="ml-4 list-disc">
                      {report.comments.map((c, idx) => (
                        <li key={idx}>
                          <span className="font-medium">
                            {c.doctor.user.firstName} {c.doctor.user.lastName}:
                          </span>{' '}
                          {c.comment}{' '}
                          <span className="text-xs text-gray-400">
                            ({new Date(c.createdAt).toLocaleString()})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientTestReports;
